import React from 'react';
import { clsx } from 'clsx';

export interface SimpleSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  options: Array<{ value: string | number; label: string }>;
  placeholder?: string;
  containerClassName?: string;
}

/**
 * SimpleSelect - Select component without Formik dependency
 */
export function SimpleSelect({
  label,
  error,
  helperText,
  options,
  placeholder,
  containerClassName,
  className,
  required,
  disabled,
  id = '',
  ...props
}: SimpleSelectProps) {
  const hasError = !!error;
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={clsx('w-full', containerClassName)}>
      {label && (
        <label
          htmlFor={selectId}
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <select
        id={selectId}
        {...props}
        disabled={disabled}
        className={clsx(
          'block w-full rounded-lg border transition-colors duration-200',
          'px-4 py-2.5 text-base',
          'text-gray-900 dark:text-white',
          'bg-white dark:bg-gray-800',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          'appearance-none bg-right bg-no-repeat',
          'cursor-pointer',
          // Ensure options dropdown has proper background in dark mode
          '[&>option]:bg-white [&>option]:text-gray-900',
          'dark:[&>option]:bg-gray-800 dark:[&>option]:text-white',
          hasError
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600',
          disabled && 'cursor-not-allowed bg-gray-50 opacity-50 dark:bg-gray-900',
          className
        )}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundPosition: 'right 0.5rem center',
          backgroundSize: '1.5em 1.5em',
          colorScheme: 'dark light',
        }}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {hasError && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {helperText && !hasError && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
